/* global React */
/* eslint-disable no-restricted-globals */
import { useEffect, useState } from 'react'
import { Grid, LinearProgress, type GridSize } from '@mui/material'
import { useMemoRef, useStyles } from 'platform/react/hook'
import useAttachmentClick from 'platform/react/hook/useAttachmentClick'
import usePreviewableAttachment from 'platform/react/hook/usePreviewableAttachment'
import Toolbar from 'platform/react/hoc/attachment/toolbar'
import Image, { type ImageState, type ImageProps, ImageIcon } from 'ui/Element/Image'

export type FileImageProps = ImageProps & {
  clickable?: boolean
  skipSkeleton?: boolean
  skipPlaceholder?: boolean
  download?: boolean
  previewableTypes?: string[] | null
  maxPreviewSize?: number | null
  placeholderProps?: Omit<ImageIcon, 'name'>
  size: ImageProps['width']
  xs?: GridSize
  sm?: GridSize
  md?: GridSize
  lg?: GridSize
  xl?: GridSize
}

/**
 * Returns the FileImage element's current theme styles.
 *
 * @param theme           the application's current theme
 * @param props           the element's properties
 * @param props.clickable whether the attachment is clickable
 * @returns               the FileImage element's styles
 */
const styles = (theme: any, { clickable }: { clickable: boolean }) => ({
  root: {
    display: 'flex',
    position: 'relative',
    justifyContent: 'center',
    height: '100%',
    cursor: clickable ? 'pointer' : 'auto',
  },
  progress: {
    left: 0,
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0.4,
    borderRadius: '0.5rem',
  },
  ...theme.custom.fileImage,
})

/**
 * FileImage element
 *
 * Used to display preview images of files. Previewable attachments are
 * fetched and displayed right away, all others are shown as a file-type
 * placeholder and only fetched (and then downloaded) on click.
 *
 * @param props                  the element's properties
 * @param props.attachment       the attachment to display
 * @param props.types            file type map used for the placeholder
 * @param props.group            attachment group of the attachment
 * @param props.file             local file to display
 * @param props.events           event handlers (`onState`, `onClick`)
 * @param props.clickable        whether the attachment reacts to clicks
 * @param props.skipSkeleton     whether to skip the loading skeleton
 * @param props.skipPlaceholder  whether to skip the file-type placeholder
 * @param props.placeholderProps styling applied to the placeholder icon
 * @param props.showProgress     whether to overlay a progress bar while loading
 * @param props.download         whether clicking downloads the attachment
 * @param props.previewableTypes types that may be previewed directly
 * @param props.maxPreviewSize   maximum size in bytes for direct previews
 * @param props.xs               grid columns on extra small screens
 * @param props.sm               grid columns on small screens
 * @param props.md               grid columns on medium screens
 * @param props.lg               grid columns on large screens
 * @param props.xl               grid columns on extra large screens
 * @returns                      the new FileImage element
 */
const FileImage = ({ attachment, types, group, events, file, ...props }: FileImageProps) => {
  const {
    clickable = true,
    skipSkeleton = false,
    skipPlaceholder = false,
    download: canDownload = true,

    placeholderProps = {},
    fit = 'contain',
    shape = 'natural',
    rounded = false,
    bordered = false,
    width = props.size,
    height = props.size,

    previewableTypes = null,
    maxPreviewSize = null,

    xs,
    sm,
    md,
    lg,
    xl,
  } = props

  const currentAttachment = attachment?.value?.name
    ? attachment
    : { value: { name: '' } }

  const classes = useStyles(styles, { clickable })() as Record<string, string>
  const isPreviewable = usePreviewableAttachment({
    attachment: currentAttachment,
    previewableTypes,
    maxPreviewSize,
  })

  const [state, setState] = useState<ImageState | null>(null)
  const progress = state?.progress ?? 0
  const isLoading = progress > 0 && progress < 100

  // Forwarding the image state to the parent. The inner image is memoized
  // against its source props, so this cannot happen in the state setter it
  // captures.
  useEffect(() => { state && events?.onState?.(state) }, [state])

  const { onClick: handleClick } = useAttachmentClick({
    attachment: currentAttachment,
    attachmentState: state,
    download: canDownload,
    handler: events?.onClick,
  })

  return (
    <Grid
      item
      xs={xs}
      sm={sm}
      md={md}
      lg={lg}
      xl={xl}
      onClick={clickable ? handleClick : undefined}
      className={classes.root}
    >
      <Image
        group={group}
        types={types}
        file={file}
        attachment={currentAttachment}
        defer={!isPreviewable}
        skeleton={!skipSkeleton}
        placeholder={skipPlaceholder ? false : placeholderProps}
        fit={fit}
        shape={shape}
        rounded={rounded}
        bordered={bordered}
        width={width}
        height={height}
        events={{ onState: setState }}
      />
    </Grid>
  )
}

export default useMemoRef(Toolbar(FileImage), (props: FileImageProps) => [
  props.attachment,
  props.group,
  props.file,
])
